/* ============================================================
   GUIDE SEARCH — the pure matcher behind the guide's search box.
   Single source of truth, reused by the view (render the dropdown),
   the reducer (clamp the result cursor) and the controller (resolve
   the selection to a tune). Deterministic; no state.

   Two match kinds:
   - PROGRAMS: substring on video title, across VISIBLE channels only
     (so hidden channels never leak through program search).
   - CHANNELS: KNOWN channels (visible / unlocked) match by substring
     on name + number forms; SECRET channels (hidden & locked) match
     ONLY on an exact full-term — so they can't be stumbled onto by a
     prefix or partial number; you must know the name.
   ============================================================ */
import type { Channel } from './types'
import { buildLiveQueue } from './live'
import { queueIndexToProgramOrdinal } from './breakResolver'

export type SearchResult =
  | { kind: 'channel'; channelIndex: number; label: string; sub: string }
  | { kind: 'program'; channelIndex: number; ordinal: number; label: string; sub: string }

const MAX_RESULTS = 40
const pad = (n: number) => (n < 10 ? '0' : '') + n

const isKnown = (ch: Channel, unlocked: number[]) => !ch.hidden || unlocked.includes(ch.number)

/** Every string that should resolve to this channel, lowercased. */
const channelIdentifiers = (ch: Channel): string[] =>
  [ch.name, ch.slug, ...(ch.keywords ?? []), `channel ${ch.number}`, `ch${ch.number}`, `${ch.number}`, `channel ${pad(ch.number)}`, pad(ch.number)]
    .filter(Boolean)
    .map((s) => s.toLowerCase())

export function searchGuide(channels: Channel[], unlockedSecrets: number[], query: string): SearchResult[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const unlocked = unlockedSecrets ?? []
  const channelHits: SearchResult[] = []
  const programHits: SearchResult[] = []

  channels.forEach((ch, channelIndex) => {
    // CHANNEL match — known: substring; secret: exact full-term.
    const ids = channelIdentifiers(ch)
    const matched = isKnown(ch, unlocked) ? ids.some((id) => id.includes(q)) : ids.some((id) => id === q)
    if (matched) channelHits.push({ kind: 'channel', channelIndex, label: ch.name, sub: `CH ${pad(ch.number)}` })

    // PROGRAM match — substring on title, VISIBLE channels only (don't leak hidden ones).
    if (!isKnown(ch, unlocked)) return
    // Search the full broadcast (adBumpers=true): ad items aren't videos (skipped below) and a
    // seconds-split video's halves share one title (deduped), so the ad setting can't change matches.
    const queue = buildLiveQueue(ch, true)
    const seen = new Set<string>()
    queue.forEach((item, qi) => {
      if (item.kind !== 'video') return
      const title = item.title ?? ''
      if (!title || seen.has(title) || !title.toLowerCase().includes(q)) return
      seen.add(title)
      programHits.push({ kind: 'program', channelIndex, ordinal: queueIndexToProgramOrdinal(queue, qi), label: title, sub: `${ch.name} · CH ${pad(ch.number)}` })
    })
  })

  return [...channelHits, ...programHits].slice(0, MAX_RESULTS)
}
